import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Col,
  Row,
  Space,
  Table,
  Card,
  Spin,
  message,
  Divider,
  Button,
  Typography,
  Select,
  Input,
  Tag,
} from "antd";
import {
  SearchOutlined,
  FilterOutlined,
  ReloadOutlined,
  FileExcelOutlined,
} from "@ant-design/icons";
import axios from "axios";
import SendLawyerDetailModal from "./modal/SendLawyerDetailModal";
import { mainlawyer } from "../../API/apiUrls";
import { exportLawyerExcel } from "./export/ExportLawyerExcel";

const { Text } = Typography;

const thb = (n) =>
  Number(n || 0).toLocaleString("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const STATUS_META = {
  SUED: { color: "red", label: "เฮียสั่งฟ้อง" },
};

const getStatusMeta = (status) =>
  STATUS_META[status] || { color: "default", label: status || "-" };

const parseAccountNumbers = (raw) => {
  if (Array.isArray(raw)) return raw;
  try {
    const parsed = JSON.parse(raw ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export default function SendLawyerPage() {
  const USERNAME = localStorage.getItem("USERNAME");
  const ROLE_ID = localStorage.getItem("ROLE_ID");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const [searchContno, setSearchContno] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [lawyerName, setLawyerName] = useState(undefined);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(mainlawyer);
      const list = res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
      const visibleList =
        ROLE_ID === "1" || ROLE_ID === "2"
          ? list
          : list.filter((item) => item.lawyer_user_id === USERNAME);
      setData(visibleList);
      setFilteredData(visibleList);
    } catch (err) {
      console.error(err);
      message.error("โหลดข้อมูลไม่สำเร็จ");
      setData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };
  const lawyerOptions = useMemo(
    () =>
      Array.from(new Set(data.map((d) => d.lawyer_name).filter(Boolean))).map(
        (name) => ({ value: name, label: name }),
      ),
    [data],
  );

  const handleFilter = useCallback(() => {
    let filtered = data;

    if (statusFilter !== "all") {
      filtered = filtered.filter((d) => d.lawsuit_status === statusFilter);
    }
    if (lawyerName) {
      filtered = filtered.filter((d) => d.lawyer_name === lawyerName);
    }

    const query = searchContno.trim().toLowerCase();
    if (query) {
      filtered = filtered.filter(
        (d) =>
          d.borr_code?.toLowerCase().includes(query) ||
          d.name?.toLowerCase().includes(query) ||
          d.account_name?.toLowerCase().includes(query),
      );
    }

    setFilteredData(filtered);
    setSelectedRowKeys([]);
  }, [data, statusFilter, lawyerName, searchContno]);

  const handleResetFilters = useCallback(() => {
    setStatusFilter("all");
    setLawyerName(undefined);
    setSearchContno("");
    setFilteredData(data);
    setSelectedRowKeys([]);
  }, [data]);

  const hasActiveFilters =
    statusFilter !== "all" || !!lawyerName || !!searchContno.trim();

  const handleExportExcel = () => {
    const rows =
      selectedRowKeys.length > 0
        ? filteredData.filter((r) => selectedRowKeys.includes(r.borr_code))
        : filteredData;

    if (rows.length === 0) {
      message.warning("ไม่มีข้อมูลสำหรับ Export");
      return;
    }
    exportLawyerExcel(rows);
  };

  const columns = [
    {
      title: "ลำดับ",
      key: "index",
      align: "center",
      width: 60,
      render: (_, __, i) => i + 1,
    },
    {
      title: "ข้อมูลลูกหนี้",
      key: "detail",
      align: "left",
      width: 260,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.7 }}>
          <div>
            <Text strong>{r.name || r.account_name || "-"}</Text>
          </div>
          <div style={{ color: "#888" }}>รหัสลูกหนี้ {r.borr_code || "-"}</div>
        </div>
      ),
    },
    {
      title: "สถานะคดี",
      key: "status",
      align: "left",
      width: 220,
      render: (_, r) => {
        const status = getStatusMeta(r.lawsuit_status);
        return (
          <div style={{ fontSize: 13, lineHeight: 1.7 }}>
            <Tag color={status.color}>{status.label}</Tag>
            <div>
              <Text strong>{r.lawyer_name || "-"}</Text>
            </div>
          </div>
        );
      },
    },
    {
      title: "ยอดหนี้ไม่รวมดอกเบี้ย ONE",
      key: "amount",
      align: "left",
      width: 220,
      render: (_, r) => (
        <div style={{ fontSize: 13, lineHeight: 1.6 }}>
          <div>
            <span style={{ color: "#888" }}>เงินต้น </span>฿
            {thb(r.principle_amount)}
          </div>
          <div>
            <span style={{ color: "#888" }}>ดอกเบี้ย SCB </span>฿
            {thb(r.total_interest_amount)}
          </div>
        </div>
      ),
    },
    {
      title: "เลขบัญชี",
      key: "accounts",
      align: "left",
      width: 180,
      render: (_, r) => (
        <Space size={4} wrap>
          {parseAccountNumbers(r.account_numbers).map((no, idx) => (
            <Tag key={`${no}-${idx}`}>{no}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "การจัดการ",
      align: "left",
      width: 180,
      render: (_, r) => <Button>ดูรายละเอียด</Button>,
    },
  ];

  return (
    <>
      <style>{`
        .row-even td { background-color: #f5f8ff !important; }
        .row-odd td  { background-color: #ffffff !important; }
      `}</style>
      <Spin spinning={loading} size="large" tip="Loading...">
        <Card style={{ marginBottom: 10 }}>
          <Divider style={{ fontSize: 20 }}>รายงานสัญญาที่ส่งทนาย</Divider>

          {/* ── แถบกรองข้อมูล ── */}
          <Card
            size="small"
            title={
              <Space>
                <FilterOutlined />
                <span>ตัวกรองข้อมูล</span>
              </Space>
            }
            style={{ marginBottom: 12, background: "#fafafa" }}
          >
            <Row gutter={[16, 12]} align="bottom">
              <Col xs={24} sm={12} md={6} lg={5}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ค้นหา (รหัสลูกหนี้ / ชื่อ)
                </Text>
                <Input
                  placeholder="เช่น BU 03444"
                  value={searchContno}
                  onChange={(e) => setSearchContno(e.target.value)}
                  onPressEnter={handleFilter}
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  allowClear
                  size="small"
                />
              </Col>

              <Col xs={24} sm={12} md={6} lg={4}>
                <Text type="secondary" style={{ fontSize: 13 }}>
                  ทนายที่รับผิดชอบ
                </Text>
                <Select
                  style={{ width: "100%" }}
                  options={lawyerOptions}
                  value={lawyerName}
                  onChange={setLawyerName}
                  popupMatchSelectWidth={false}
                  allowClear
                  showSearch
                  placeholder="ทั้งหมด"
                  size="large"
                  disabled={ROLE_ID > 2}
                />
              </Col>

              <Col flex="auto" style={{ textAlign: "right" }}>
                <Space>
                  <Button
                    color="orange"
                    variant="outlined"
                    icon={<FilterOutlined />}
                    onClick={handleFilter}
                  >
                    กรองข้อมูล
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    onClick={handleResetFilters}
                    color="red"
                    variant="outlined"
                  >
                    ล้างตัวกรอง
                  </Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* ── สรุปตัวกรองที่ใช้อยู่ ── */}
          {hasActiveFilters && (
            <Card
              size="small"
              style={{ background: "#f0f5ff", marginBottom: 12 }}
            >
              <Space split="|" size="large" wrap>
                <span>
                  <strong>ผลการค้นหา:</strong> {filteredData.length} รายการ
                </span>
                {searchContno && (
                  <span>
                    <strong>ค้นหา:</strong> "{searchContno}"
                  </span>
                )}
                {lawyerName && (
                  <span>
                    <strong>ทนายที่รับผิดชอบ:</strong> {lawyerName}
                  </span>
                )}
              </Space>
            </Card>
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Text
              strong
              style={{
                margin: "10px 0",
              }}
            >
              จำนวนสัญญาทั้งหมด : {data.length} เคส
              {selectedRowKeys.length > 0 &&
                ` (เลือก ${selectedRowKeys.length} รายการ)`}
            </Text>
            <Button
              icon={<FileExcelOutlined />}
              onClick={handleExportExcel}
              color="green"
              variant="outlined"
            >
              Export Excel
            </Button>
          </div>
          <Table
            style={{ marginTop: 10 }}
            size="small"
            columns={columns}
            dataSource={filteredData}
            rowKey="borr_code"
            onRow={(r) => ({ onClick: () => setSelected(r) })}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            rowClassName={(_, index) =>
              index % 2 === 0 ? "row-even" : "row-odd"
            }
            scroll={{ x: 1100 }}
            pagination={{
              defaultPageSize: 50,
              showSizeChanger: true,
              showTotal: (t) => `ทั้งหมด ${t} รายการ`,
              pageSizeOptions: ["10", "20", "50", "100"],
            }}
          />
        </Card>
      </Spin>

      {selected && (
        <SendLawyerDetailModal
          item={selected}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}
